import type { IPhoneNumber } from "../components/staff/PhoneNumber";

function stripBasecodeFromPhoneString(phone: string): string {
  return phone
    .split(/\s+/)
    .filter((part) => !/^[A-Z]{2,3}$/.test(part))
    .join(" ")
    .trim();
}

type PersonPhoneFields = {
  countryCodePhoneNumber?: string | null;
  phoneNumber?: string | IPhoneNumber | null;
};

const personPhoneDisplayOptions = { includeBasecode: false } as const;

/** Cosmos person docs store phone as a string or `{ code, phone, basecode }`. */
export function normalizePersonPhoneNumber(
  value: unknown
): string | IPhoneNumber | undefined {
  if (value == null || value === "") {
    return undefined;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") {
      return undefined;
    }
    return trimmed;
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const code = record.code != null ? String(record.code).trim() : "";
    const phone = record.phone != null ? String(record.phone).trim() : "";
    const basecode = record.basecode != null ? String(record.basecode).trim() : "";
    if (!code && !phone && !basecode) {
      return undefined;
    }
    return { code, phone, basecode };
  }

  return undefined;
}

/**
 * Person records may store `phoneNumber` as a string (with separate country code)
 * or as an {@link IPhoneNumber} object after API migration.
 * Omits ISO basecode (e.g. "KZ") — shows dial code and number only.
 */
export function formatPersonPhoneForDisplay(
  person: PersonPhoneFields | undefined | null,
  fallback?: string | IPhoneNumber | null
): string {
  const phone = normalizePersonPhoneNumber(person?.phoneNumber);
  if (phone != null && phone !== "") {
    if (typeof phone === "object") {
      const formatted = formatPhoneForDisplay(phone, personPhoneDisplayOptions);
      if (formatted) {
        return formatted;
      }
    } else {
      const formatted = [person?.countryCodePhoneNumber, phone]
        .map((s) => String(s ?? "").trim())
        .filter(Boolean)
        .join(" ");
      if (formatted) {
        return formatted;
      }
    }
  }
  return formatPhoneForDisplay(fallback, personPhoneDisplayOptions);
}

/**
 * Text for UI when `phoneNumber` can come as string (legacy) or as API object.
 */
export function formatPhoneForDisplay(
  phone: string | IPhoneNumber | undefined | null,
  options?: { includeBasecode?: boolean }
): string {
  const includeBasecode = options?.includeBasecode ?? true;
  if (phone == null) {
    return "";
  }
  if (typeof phone === "string") {
    const trimmed = phone.trim();
    return includeBasecode ? trimmed : stripBasecodeFromPhoneString(trimmed);
  }
  const { code = "", basecode = "", phone: num = "" } = phone;
  const parts = includeBasecode ? [code, basecode, num] : [code, num];
  return parts
    .map((s) => String(s ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
}

/**
 * Digits-only international form for vCard TEL (e.g. +5212311123).
 * Returns "" if there are no digits (avoids a lone "+" in the file).
 */
export function formatPhoneForVCard(
  phone: string | IPhoneNumber | undefined | null
): string {
  if (phone == null) {
    return "";
  }
  const raw =
    typeof phone === "string"
      ? phone
      : [phone.code, phone.phone, phone.basecode]
          .map((s) => String(s ?? "").trim())
          .join(" ");
  const cleaned = raw.replace(/\D/g, "");
  if (!cleaned) {
    return "";
  }
  return `+${cleaned}`;
}

/**
 * Prefer the member's direct phone (profile); fall back to main office.
 * Office lines are often a firm switchboard — the vCard should match the person's number when set.
 */
export function resolvePhoneForVCard(
  personPhone: string | IPhoneNumber | undefined | null,
  officePhone: string | IPhoneNumber | undefined | null
): string {
  const fromPerson = formatPhoneForVCard(personPhone);
  if (fromPerson) {
    return fromPerson;
  }
  return formatPhoneForVCard(officePhone);
}
